import type { EngineInput } from '@casino/craps-engine';
import { newGame, nextSeq, advanceTurn, type RecordingState } from './recordingState.js';
import {
  getOrCreateStreamCode,
  getStreamCode,
  getDisplayName,
  isBroadcasting,
  setBroadcasting,
} from './streamIdentity.js';

type WireEvent = { type: string; [k: string]: unknown };

// ─── Turn-structured durable log shapes ────────────────────────────────────
type Keyframe = { phase: string; point?: number; bets: unknown[]; history: unknown[]; bankroll: number };
type TurnHeaderItem = { kind: 'turn'; turn_id: number; keyframe: Keyframe; bankroll_start: number; started_at: number };
type EventItem = { kind: 'event'; turn_id: number; seq: number; client_ts: number; type: string; [k: string]: unknown };
type LogItem = TurnHeaderItem | EventItem;

interface StartGameArgs {
  gameId: string; deviceId: string; game: 'craps';
  tableKey: 'standard' | 'high-limit' | 'whale';
  rules: unknown; replaySchemaVersion: number;
  keyframe: Keyframe; bankrollStart: number;
}
interface BoundaryArgs { keyframe: Keyframe; bankrollStart: number }
interface ResumeGameArgs {
  gameId: string; deviceId: string; game: 'craps';
  tableKey: 'standard' | 'high-limit' | 'whale';
  rules: unknown; replaySchemaVersion: number;
  turnId: number; lastSeq: number;
  /** Current felt snapshot. When provided while broadcasting, re-opens the DO
   *  buffer for the resumed turn (the live seq restarts from 0). */
  keyframe?: unknown;
}

export interface SessionRecorder {
  /** Begin recording a fresh game: sets the header + first turn keyframe. */
  startGame(args: StartGameArgs): void;
  /** Restore recording counters from a saved state after a reload (no turn header emitted). */
  resumeGame(args: ResumeGameArgs): void;
  /** Record a single engine input as a turn-tagged event. */
  recordInput(input: EngineInput): void;
  /** Seven-out boundary: open the next turn with its keyframe. */
  boundary(args: BoundaryArgs): void;
  /** Open the DO WebSocket for live streaming (call when the user taps Broadcast). */
  startStreaming(): Promise<void>;
  /** Stop broadcasting: close the WS and clear the broadcasting flag. */
  stopStreaming(): void;
  /** Provide a getter for the engine's current serialized felt, so broadcast
   *  toggled on mid-game can bootstrap the live buffer with the current state. */
  setKeyframeSource(fn: () => unknown): void;
  /** Force-close the recorder's WS and clear streaming state. */
  endSession(): Promise<void>;
  /** Builds a watch URL `${origin}/?watch=<code>` if a stream code exists. */
  shareUrl(): string | null;
  /** Debug snapshot of the recorder's runtime state. */
  debug(): { wsState: string; helloDone: boolean; bufferLength: number; streamCode: string | null; liveSeq: number };

  // Test-only helpers
  flushForTesting(): Promise<void>;
}

export interface RecorderOptions {
  endpoint?: string;
  wsFactory?: (url: string) => WebSocket;
  maxBuffer?: number;
  /** Injected fetch for testing; defaults to global fetch. */
  fetchFn?: (url: string, init?: RequestInit) => Promise<{ status: number }>;
  /** Interval between automatic durable flushes (ms). Default 1500. */
  flushIntervalMs?: number;
  /** Called whenever the recording state changes (for persisting across reloads). */
  onState?: (state: RecordingState) => void;
}

export function createSessionRecorder(opts: RecorderOptions = {}): SessionRecorder {
  const endpoint = opts.endpoint ?? '/api/games';
  const wsFactory = opts.wsFactory ?? ((url) => new WebSocket(url));
  const maxBuffer = opts.maxBuffer ?? 1000;
  const fetchFn = opts.fetchFn ?? ((url, init) => fetch(url, init));
  const flushIntervalMs = opts.flushIntervalMs ?? 1500;

  let ws: WebSocket | null = null;
  let helloDone = false;
  let backoff = 250;                  // ms, exponential up to 5_000
  let intentionallyClosed = false;    // set by endSession/stopStreaming to suppress reconnect
  let connecting = false;             // in-flight guard for startStreaming
  let keepaliveTimer: ReturnType<typeof setInterval> | null = null;

  // Streaming state
  let streaming = false;
  // The device id, re-introduced for the publish URL + register call. Set from
  // startGame/resumeGame args.
  let deviceId = '';

  // Durable buffer: turn-structured items pending HTTP POST to /log
  const durable: LogItem[] = [];
  // Turn/seq recording state (held for the active page session; bridge persists into ActiveGame).
  let rec: RecordingState | null = null;
  // Game header sent with every durable batch.
  let header: { game_id: string; device_id: string; game: string; table_key: string; rules: unknown; replay_schema_version: number } | null = null;
  let flushTimer: ReturnType<typeof setTimeout> | null = null;
  let flushing = false;

  const emitState = () => { if (rec) opts.onState?.(rec); };

  // ─── Live turn buffer (DO-buffer mirror, for resync/reconnect replay) ──────
  // The producer keeps a mirror of the DO's current-turn buffer so it can
  // replay from any server position on hello/resync. The live `seq` is the
  // DO-buffer position within the current turn (1-based, contiguous) and is a
  // SEPARATE counter from the durable seq — it RESETS to 0 on every boundary.
  let liveTurn: { gameId: string; turnId: number; keyframe: unknown; events: Array<{ seq: number; client_ts: number; event: WireEvent }> } | null = null;
  let liveSeq = 0; // DO-buffer position within the current turn; resets on each boundary
  // Supplied by the bridge: returns the engine's CURRENT serialized felt. Used to
  // bootstrap the live buffer when broadcast is toggled on mid-game.
  let getKeyframe: (() => unknown) | null = null;

  const wsReady = () => !!ws && helloDone && ws.readyState === 1;

  const sendBoundaryLive = (gameId: string, turnId: number, keyframe: unknown) => {
    liveTurn = { gameId, turnId, keyframe, events: [] };
    liveSeq = 0;
    if (wsReady() && header) ws!.send(JSON.stringify({ kind: 'boundary', gameId, turnId, keyframe, game: header.game, tableKey: header.table_key, rules: header.rules }));
  };
  const sendEventLive = (event: WireEvent, client_ts: number) => {
    if (!liveTurn) return;
    liveSeq += 1;
    const entry = { seq: liveSeq, client_ts, event };
    liveTurn.events.push(entry);
    if (wsReady()) ws!.send(JSON.stringify({ kind: 'event', gameId: liveTurn.gameId, turnId: liveTurn.turnId, seq: entry.seq, client_ts, event }));
  };
  // Resend the current turn from a server position (boundary if server has nothing).
  const replayLiveFrom = (serverLastSeq: number) => {
    if (!liveTurn || !wsReady()) return;
    if (serverLastSeq <= 0) ws!.send(JSON.stringify(header ? { kind: 'boundary', gameId: liveTurn.gameId, turnId: liveTurn.turnId, keyframe: liveTurn.keyframe, game: header.game, tableKey: header.table_key, rules: header.rules } : { kind: 'boundary', gameId: liveTurn.gameId, turnId: liveTurn.turnId, keyframe: liveTurn.keyframe }));
    for (const e of liveTurn.events) if (e.seq > serverLastSeq) ws!.send(JSON.stringify({ kind: 'event', gameId: liveTurn.gameId, turnId: liveTurn.turnId, seq: e.seq, client_ts: e.client_ts, event: e.event }));
  };

  // ─── Durable flush (HTTP, idempotent server-side via INSERT OR IGNORE) ────

  const flushDurable = async (): Promise<void> => {
    if (flushing || durable.length === 0 || !header) return;
    flushing = true;
    const batch = durable.slice();
    try {
      const res = await fetchFn(`${endpoint}/${header.game_id}/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...header, items: batch }),
      });
      if (res.status >= 200 && res.status < 300) durable.splice(0, batch.length);
    } catch { /* keep buffer; retry next flush */ }
    finally { flushing = false; }
  };

  const scheduleFlush = () => {
    if (flushTimer) return;
    flushTimer = setTimeout(() => { flushTimer = null; void flushDurable(); }, flushIntervalMs);
  };

  // ─── WebSocket connection ─────────────────────────────────────────────────

  const endSession = async (): Promise<void> => {
    intentionallyClosed = true;
    if (keepaliveTimer) { clearInterval(keepaliveTimer); keepaliveTimer = null; }
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
    ws?.close();
    ws = null;
    helloDone = false;
    streaming = false;
  };

  const connect = async (streamCode: string) => {
    intentionallyClosed = false;
    // Open the publish WS directly — no 409 pre-flight. The DO is single-owner
    // and auto-displaces any stale publisher, so a reconnecting producer never
    // fights its own un-reaped ghost ("already publishing").
    const url = `${location.origin.replace(/^http/, 'ws')}/api/streams/${streamCode}/publish?device_id=${encodeURIComponent(deviceId)}`;
    const sock = wsFactory(url);
    ws = sock;
    // Keepalive: most NATs / proxies close idle WebSockets after ~60-120s.
    // Send a tiny ping every 25s so the connection stays open even when the
    // producer is just sitting at the table without rolling.
    if (keepaliveTimer) clearInterval(keepaliveTimer);
    keepaliveTimer = setInterval(() => {
      if (ws && ws.readyState === 1 /* OPEN */) {
        try { ws.send(JSON.stringify({ kind: 'ping' })); } catch { /* ignore */ }
      }
    }, 25_000);
    sock.onmessage = (ev: MessageEvent<string>) => {
      const frame = JSON.parse(ev.data) as { kind: string; [k: string]: unknown };
      if (frame.kind === 'ping') return; // keepalive echo
      if (frame.kind === 'hello') {
        helloDone = true;
        backoff = 250;
        const cur = (frame as { current?: { gameId: string; turnId: number; lastSeq: number } | null }).current ?? null;
        if (liveTurn) {
          if (cur && cur.gameId === liveTurn.gameId && cur.turnId === liveTurn.turnId) replayLiveFrom(cur.lastSeq);
          else replayLiveFrom(0); // server has nothing/different turn → resend boundary + all events
        }
      } else if (frame.kind === 'ack') {
        // no-op: liveTurn retains the current turn for resync; it's cleared on the next boundary
      } else if (frame.kind === 'resync') {
        if (liveTurn && (frame as { turnId?: number }).turnId === liveTurn.turnId) {
          replayLiveFrom((frame as { server_last_seq?: number }).server_last_seq ?? 0);
        }
      }
    };
    // Auto-reconnect with exponential backoff.
    sock.onclose = (ev?: { code?: number }) => {
      if (sock !== ws) return; // a newer socket already superseded this one
      helloDone = false;
      if (keepaliveTimer) { clearInterval(keepaliveTimer); keepaliveTimer = null; }
      if (intentionallyClosed) return;
      if (ev?.code === 4001) {
        // Another connection (e.g. a second tab) took over this stream. Yield
        // silently rather than reconnecting and ping-ponging the takeover.
        streaming = false;
        return;
      }
      const code = getStreamCode();
      if (!code || !streaming) return;
      setTimeout(() => { void connect(code); }, backoff);
      backoff = Math.min(backoff * 2, 5000);
    };
  };

  // ─── Visibility / unload flush ────────────────────────────────────────────

  if (typeof document !== 'undefined') {
    // Best-effort sendBeacon flush — used by both pagehide and visibilitychange.
    const beaconFlush = () => {
      if (!header || durable.length === 0) return;
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const batch = durable.slice();
        const n = batch.length;
        const payload = JSON.stringify({ ...header, items: batch });
        const sent = navigator.sendBeacon(
          `${endpoint}/${header.game_id}/log`,
          new Blob([payload], { type: 'application/json' }),
        );
        // Only remove the exact events we snapshotted — mirrors flushDurable's
        // splice-by-count so events appended during the build are not dropped.
        if (sent) durable.splice(0, n);
      } else {
        void flushDurable();
      }
    };
    // visibilitychange fires while document.hidden may be true (tab backgrounded).
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) beaconFlush();
    });
    // pagehide fires on navigation/close while document.hidden is still false,
    // so it must always flush unconditionally.
    window.addEventListener('pagehide', beaconFlush);
  }

  const startStreaming = async (): Promise<void> => {
    // Guard against overlapping calls (sticky auto-resume racing a manual tap):
    // without it, two callers could both pass the `!ws` check below and open two
    // publisher sockets — the DO's single-publisher 409 would then spuriously pop
    // the multi-tab modal at the same player.
    if (ws || connecting) { streaming = true; setBroadcasting(true); return; }
    connecting = true;
    const streamCode = getOrCreateStreamCode();
    try {
      await fetchFn(`/api/streams/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stream_code: streamCode, device_id: deviceId, display_name: getDisplayName() }),
      });
    } catch { /* best effort */ }
    streaming = true;
    setBroadcasting(true);
    try { if (!ws) await connect(streamCode); }
    finally { connecting = false; }
    // Broadcast toggled on MID-GAME: startGame already ran before streaming, so
    // the DO has no buffer for the current turn and watchers see "offline" until
    // the next seven-out. Bootstrap it with the current felt; the hello reconcile
    // (replayLiveFrom) then resends boundary + any events to the DO.
    if (rec && !liveTurn && getKeyframe) {
      sendBoundaryLive(rec.gameId, rec.turnId, getKeyframe());
    }
  };

  return {
    startGame(args: StartGameArgs) {
      deviceId = args.deviceId;
      rec = newGame(args.gameId);
      header = {
        game_id: args.gameId,
        device_id: args.deviceId,
        game: args.game,
        table_key: args.tableKey,
        rules: args.rules,
        replay_schema_version: args.replaySchemaVersion,
      };
      durable.push({
        kind: 'turn',
        turn_id: rec.turnId,
        keyframe: args.keyframe,
        bankroll_start: args.bankrollStart,
        started_at: Date.now(),
      });
      scheduleFlush();
      emitState();
      // Live: auto-resume the broadcast if the user was broadcasting before a
      // reload, then open the DO buffer for turn 1. Open the live turn buffer
      // synchronously whenever we intend to broadcast — `streaming` flips true
      // asynchronously inside startStreaming (after the register POST), so it
      // isn't reliably set yet here; the buffered boundary is then resent once
      // the WS connects and says hello.
      const willStream = streaming || isBroadcasting();
      if (isBroadcasting() && !ws) void startStreaming();
      if (willStream) sendBoundaryLive(args.gameId, rec.turnId, args.keyframe);
    },

    resumeGame(args: ResumeGameArgs) {
      deviceId = args.deviceId;
      rec = { gameId: args.gameId, turnId: args.turnId, lastSeq: args.lastSeq };
      header = {
        game_id: args.gameId,
        device_id: args.deviceId,
        game: args.game,
        table_key: args.tableKey,
        rules: args.rules,
        replay_schema_version: args.replaySchemaVersion,
      };
      emitState();
      // Live: auto-resume the broadcast across a reload. Re-open the DO buffer
      // for the resumed turn with the current felt — the live seq restarts even
      // though the durable turn/seq continue. As in startGame, open the buffer
      // synchronously since `streaming` flips true asynchronously.
      const willStream = streaming || isBroadcasting();
      if (isBroadcasting() && !ws) void startStreaming();
      if (willStream && args.keyframe !== undefined) sendBoundaryLive(args.gameId, args.turnId, args.keyframe);
    },

    recordInput(input: EngineInput) {
      if (!rec) return;
      const adv = nextSeq(rec); rec = adv.state;
      const client_ts = Date.now();
      durable.push({
        kind: 'event',
        turn_id: rec.turnId,
        seq: adv.seq,
        client_ts,
        ...(input as Record<string, unknown>),
      } as EventItem);
      while (durable.length > maxBuffer) durable.shift();
      scheduleFlush();
      emitState();
      // Append to the live mirror whenever a turn buffer is open (set by
      // startGame/boundary/resumeGame while broadcasting). Guarding on
      // `liveTurn` rather than `streaming` covers the brief window where the
      // broadcast is auto-resuming and `streaming` hasn't flipped true yet.
      if (liveTurn) sendEventLive(input as WireEvent, client_ts);
    },

    boundary(args: BoundaryArgs) {
      if (!rec) return;
      rec = advanceTurn(rec);
      durable.push({
        kind: 'turn',
        turn_id: rec.turnId,
        keyframe: args.keyframe,
        bankroll_start: args.bankrollStart,
        started_at: Date.now(),
      });
      scheduleFlush();
      emitState();
      // Open the next live turn only if a live buffer is already running for
      // this broadcast (don't spuriously start one mid-game if the user never
      // tapped broadcast).
      if (liveTurn || streaming) sendBoundaryLive(rec.gameId, rec.turnId, args.keyframe);
    },

    startStreaming,

    stopStreaming() {
      streaming = false;
      setBroadcasting(false);
      intentionallyClosed = true;
      ws?.close();
      ws = null;
      helloDone = false;
    },

    setKeyframeSource(fn) {
      getKeyframe = fn;
    },

    endSession,

    shareUrl() {
      const code = getStreamCode();
      return code ? `${location.origin}/?watch=${code}` : null;
    },

    debug() {
      const state = !ws ? 'null'
        : ws.readyState === 0 ? 'connecting'
        : ws.readyState === 1 ? 'open'
        : ws.readyState === 2 ? 'closing'
        : 'closed';
      return {
        wsState: state,
        helloDone,
        bufferLength: durable.length,
        streamCode: getStreamCode(),
        liveSeq,
      };
    },

    flushForTesting() {
      return flushDurable();
    },
  };
}
